/**
 * @module utils/vector
 * 2-D vector math helpers.
 *
 * All functions operate on plain `{ x, y }` objects to avoid the overhead of
 * class instances in the hot path (tracking + physics update every frame).
 */

/** Immutable 2-D vector. */
export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/** Returns the sum of two vectors. */
export function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

/** Returns the difference a − b. */
export function sub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

/** Returns the vector scaled by a scalar. Adds 0 to normalise −0 to +0. */
export function scale(v: Vec2, s: number): Vec2 {
  return { x: v.x * s + 0, y: v.y * s + 0 };
}

/** Returns the Euclidean length of a vector. */
export function length(v: Vec2): number {
  return Math.hypot(v.x, v.y);
}

/** Returns the squared length (cheaper than {@link length} when comparing). */
export function lengthSq(v: Vec2): number {
  return v.x * v.x + v.y * v.y;
}

/** Returns the unit vector, or { x: 0, y: 0 } if the vector is zero. */
export function normalize(v: Vec2): Vec2 {
  const len = length(v);
  if (len === 0) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

/** Returns the dot product of two vectors. */
export function dot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}

/** Returns the Euclidean distance between two points. */
export function distance(a: Vec2, b: Vec2): number {
  return length(sub(a, b));
}

/** Linearly interpolates between a and b by t ∈ [0, 1]. */
export function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}
